/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useCallback, useContext, useMemo, useRef } from "react";

type ConversationIdContextValue = {
  currentIdRef: React.MutableRefObject<string | undefined>;
  ensureConversationId: () => Promise<string | undefined>;
  setEnsureConversationId: (fn?: (() => Promise<string | undefined>) | null) => void;
};

const ConversationIdContext = createContext<ConversationIdContextValue | null>(null);

export const ConversationIdProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const currentIdRef = useRef<string | undefined>(undefined);
  const ensureRef = useRef<() => Promise<string | undefined>>(async () => currentIdRef.current);

  const setEnsureConversationId = useCallback((fn?: (() => Promise<string | undefined>) | null) => {
    console.log("[ConversationContext] setEnsureConversationId", {
      hasFn: !!fn,
      fnType: fn ? typeof fn : "null",
    });
    ensureRef.current = fn ?? (async () => currentIdRef.current);
  }, []);

  const ensureConversationId = useCallback(async () => {
    console.log("[ConversationContext] ensureConversationId called", {
      currentId: currentIdRef.current,
    });
    const result = await ensureRef.current();
    console.log("[ConversationContext] ensureConversationId result", result);
    return result;
  }, []);

  const value = useMemo(
    () => ({ currentIdRef, ensureConversationId, setEnsureConversationId }),
    [ensureConversationId, setEnsureConversationId],
  );

  return <ConversationIdContext.Provider value={value}>{children}</ConversationIdContext.Provider>;
};

const fallbackRef = { current: undefined as string | undefined };
const fallbackEnsure = async () => fallbackRef.current;
const noop = (_?: (() => Promise<string | undefined>) | null) => undefined;

export const useConversationContext = () => {
  const ctx = useContext(ConversationIdContext);
  if (!ctx) {
    return {
      currentIdRef: fallbackRef,
      ensureConversationId: fallbackEnsure,
      setEnsureConversationId: noop,
    } satisfies ConversationIdContextValue;
  }
  return ctx;
};

export const useConversationIdRef = () => useConversationContext().currentIdRef;
